/**
 * Phase 22 Plan 22-03 — AddSitesDialog
 *
 * Multi-select active sites NOT already linked to the WO.
 * On confirm → POST /api/workorders/:id/sites/add.
 */

import { useQuery } from '@tanstack/react-query';
import { useState, type JSX } from 'react';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { useAddSitesMutation } from '@/hooks/useWorkOrderQueries';
import { toast } from '@/lib/toast';
import { listSitesRequest } from '@/services/sites';
import type { PaginatedResult } from '@/types/roles';
import type { SiteListItem } from '@/types/sites';
import { isActiveSiteLink, type WorkOrderDetail } from '@/types/work-orders';

interface AddSitesDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly workOrder: WorkOrderDetail;
}

export function AddSitesDialog({ open, onOpenChange, workOrder }: AddSitesDialogProps): JSX.Element {
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());

  const addMutation = useAddSitesMutation();

  const sitesQuery = useQuery<PaginatedResult<SiteListItem>>({
    queryKey: ['sites', 'active', 'forAddSitesDialog'],
    queryFn: () => listSitesRequest({ status: 'active', page: 1, pageSize: 200 }),
    enabled: open,
    staleTime: 30_000,
  });

  const linkedIds = new Set(
    (workOrder.sites ?? []).filter(isActiveSiteLink).map((s) => s.siteId),
  );

  const availableSites = (sitesQuery.data?.items ?? []).filter((s) => !linkedIds.has(s.id));

  function toggle(id: number): void {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }

  function handleConfirm(): void {
    if (selectedIds.size === 0) return;
    addMutation.mutate(
      { id: workOrder.id, body: { siteIds: [...selectedIds] } },
      {
        onSuccess: () => {
          toast.success('Sites added');
          onOpenChange(false);
        },
        onError: (err) => {
          toast.error('Failed to add sites', { description: err.detail ?? err.message });
        },
      },
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="add-sites-dialog">
        <DialogHeader>
          <DialogTitle>Add Sites</DialogTitle>
          <DialogDescription>
            Select sites to add to Work Order {workOrder.woNumber}.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 max-h-64 overflow-y-auto py-2">
          {sitesQuery.isLoading && (
            <p className="text-sm text-muted-foreground">Loading sites…</p>
          )}
          {availableSites.length === 0 && sitesQuery.isSuccess && (
            <p className="text-sm text-muted-foreground" data-testid="add-sites-none-available">
              No additional sites available to add.
            </p>
          )}
          {availableSites.map((s) => (
            <div key={s.id} className="flex items-center gap-3">
              <Checkbox
                id={`add-site-${String(s.id)}`}
                checked={selectedIds.has(s.id)}
                onCheckedChange={() => { toggle(s.id); }}
                data-testid={`add-site-checkbox-${String(s.id)}`}
              />
              <Label htmlFor={`add-site-${String(s.id)}`} className="font-normal cursor-pointer">
                {s.name}
              </Label>
            </div>
          ))}
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => { onOpenChange(false); }}
            disabled={addMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleConfirm}
            disabled={selectedIds.size === 0 || addMutation.isPending}
            data-testid="add-sites-confirm-btn"
          >
            {addMutation.isPending ? 'Adding…' : 'Add Selected'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
